import { comparisonColumns, comparisonEvents } from '@/mocks/homeData';

type EventItem = (typeof comparisonEvents)[number];

function HeatBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-background-300 overflow-hidden">
        <div
          className={value >= 80 ? 'h-full bg-primary-500' : 'h-full bg-accent-500'}
          style={{ width: `${value}%` }}
        ></div>
      </div>
      <span className="text-xs font-medium text-foreground-700 w-8 text-right">{value}</span>
    </div>
  );
}

function renderValue(event: EventItem, key: string) {
  if (key === 'heat') return <HeatBar value={event.heat} />;
  return <span className="text-sm text-foreground-700">{event[key as keyof EventItem] as string}</span>;
}

export default function Comparison() {
  return (
    <section id="compare" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <span className="text-xs font-medium tracking-widest text-primary-600 uppercase">
              同类事件对比
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3 mb-4">
              三次规则调整，同屏对照
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              下面三起事件性质相近，都围绕"平台单方面调整规则"展开。把它们并排放在一起，
              回应方式与结果走向上的差异会比单独看要明显得多。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-600">
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              <span className="w-3 h-3 rounded-sm bg-primary-500"></span>高关注度（≥80）
            </span>
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              <span className="w-3 h-3 rounded-sm bg-accent-500"></span>一般关注度
            </span>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto border border-background-200 rounded-lg">
          <table className="w-full border-collapse min-w-[860px]">
            <thead>
              <tr className="bg-background-100">
                <th className="text-left align-bottom p-5 font-heading text-sm font-semibold text-foreground-500 border-b border-background-200 w-[160px]">
                  对比维度
                </th>
                {comparisonEvents.map((event) => (
                  <th
                    key={event.name}
                    className={`text-left align-bottom p-5 border-b border-l border-background-200 ${
                      event.highlight ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-heading text-base font-semibold text-foreground-950">
                        {event.name}
                      </span>
                      {event.highlight && (
                        <span className="text-[10px] font-medium text-primary-700 bg-primary-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                          重点
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-foreground-500">{event.period}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonColumns.map((col) => (
                <tr key={col.key} className="hover:bg-background-100/60 transition-colors">
                  <th className="text-left align-middle p-5 font-medium text-sm text-foreground-800 border-b border-background-200 bg-background-50">
                    {col.label}
                  </th>
                  {comparisonEvents.map((event) => (
                    <td
                      key={event.name + col.key}
                      className={`align-middle p-5 border-b border-l border-background-200 ${
                        event.highlight ? 'bg-primary-50/50' : ''
                      }`}
                    >
                      {renderValue(event, col.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-4">
          {comparisonEvents.map((event) => (
            <article
              key={event.name}
              className={`rounded-lg border p-5 ${
                event.highlight ? 'border-primary-300 bg-primary-50' : 'border-background-200 bg-background-100'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading text-base font-semibold text-foreground-950">{event.name}</h3>
                {event.highlight && (
                  <span className="text-[10px] font-medium text-primary-700 bg-primary-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                    重点
                  </span>
                )}
              </div>
              <span className="text-xs text-foreground-500">{event.period}</span>
              <dl className="mt-4 space-y-3">
                {comparisonColumns.map((col) => (
                  <div key={col.key} className="flex items-start gap-3">
                    <dt className="w-20 shrink-0 text-xs text-foreground-500 pt-0.5">{col.label}</dt>
                    <dd className="flex-1">{renderValue(event, col.key)}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}